import { memo, useCallback } from "react";
import { type ProductGroup } from "../../../Search/models";
import { useDispatch, useSelector } from "react-redux";
import { selectProductGroupCountMap } from "../../../Search/redux/searchSelectors";
import { type RootState } from "../../../redux/store";
import LinearInfo from "../../../Configurators/Linear/components/LinearInfo";
import { selectProductGroupWithUrl } from "../../../Search/redux/actions";
import { CheckboxWithLabel } from "../../../common/components/CheckboxWithLabel";

export const SATaxonomyFilter = memo(() => {
  const filteredTaxonomy = useSelector(selectTaxonomy);

  const productGroupCount = useSelector(selectProductGroupCountMap);
  const selectedProductGroups = useSelector(selectSelectedProductGroups);

  const dispatch = useDispatch();
  const onSelectGroup = useCallback((code: string) => dispatch(selectProductGroupWithUrl(code)), []);

  if (!filteredTaxonomy[0]) {
    return null;
  }

  const taxonomy = filteredTaxonomy[0];

  return (
    <div className={"scha__sa__taxonomy-container"}>
      {taxonomy.children.map((subGroup, index) => (
        <TaxonomyFilterItem
          group={subGroup}
          productGroupCount={productGroupCount}
          onSelectGroup={onSelectGroup}
          selectedProductGroups={selectedProductGroups}
          key={index}
        />
      ))}
    </div>
  );
});

interface TaxonomyFilterItemProps {
  group: ProductGroup;
  productGroupCount: Dict<number>;
  onSelectGroup: (code: string) => void;
  selectedProductGroups: string[];
}

const TaxonomyFilterItem = ({
  group,
  onSelectGroup,
  productGroupCount,
  selectedProductGroups,
}: TaxonomyFilterItemProps) => {
  return (
    <div className="scha__sa__taxonomy-group">
      <div className="scha__sa__taxonomy-group__title">
        <span>{group.name}</span>
        <LinearInfo info={group.description} popupClassName={"scha__lc__external-content"} />
      </div>
      <div className="scha__sa__taxonomy-group__list">
        {group.children.map((subSubItem) => {
          const isDisabled = !(productGroupCount[subSubItem.fullFacetUid] > 0);
          const isSelected = selectedProductGroups.includes(subSubItem.fullFacetUid);
          return (
            <CheckboxWithLabel
              key={subSubItem.fullFacetUid}
              className={"flex-align-center"}
              checked={isSelected}
              onCheck={() => onSelectGroup(subSubItem.fullFacetUid)}
              data-test-id={`SACheckbox-${subSubItem.fullFacetUid}`}
              disabled={isDisabled && !isSelected}
            >
              {subSubItem.name}
            </CheckboxWithLabel>
          );
        })}
      </div>
    </div>
  );
};

function selectTaxonomy(state: RootState) {
  return state.srp.fullTaxonomy;
}

const selectSelectedProductGroups = (state: RootState) => state.srp.selectedProductGroups;
